import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import nexumLogo from "../../../assets/nexumLogo.png";
import Button2 from "../../../shared/components/button/Button2";
import { getJWTDecoded } from "../../../../utils/userUtils";
import marketplaceApiClient from "../../../store/shared/api/marketplaceApiClient";
import { ApiErrorHandler } from "../../../../errorHandler";
import { AxiosResponse, AxiosError } from "axios";
import { useGlobalContext } from "../../../context/GlobalContext";
import { useAuth } from "../../../hooks/useAuth";
import { ErroObj } from "../../../shared/interfaces/model/errorObj.interface";


const MarketplaceLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenId, setTokenId] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("awaisk120@gmail.com"); // Default test email
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const { updateTenantData } = useGlobalContext();
  const { setTenantData } = useAuth();

  // Check for tokenid in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("tokenid");
    
    if (token) {
      setTokenId(token);
    } else {
      // For testing purposes, you can use a test token
      setTokenId("test-marketplace-token");
    }
  }, []);

  // Handle marketplace login
  const handleMarketplaceLogin = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      // const URL = `${baseUrl}/marketplaceLogin?tokenid=${tokenId}`;
      // const URL = `https://europe-west6-nxm-prj-svil-saas-portal.cloudfunctions.net/marketplaceLogin?tokenid=${tokenId}`;
      const URL = `http://localhost:8080/marketplaceLogin?tokenid=${tokenId}`;
      
      const response = await marketplaceApiClient
        .get(URL)
        .then((response: AxiosResponse) => response.data)
        .catch((e: AxiosError) => {
          // Handle 401 errors specially for marketplace login
          if (e.response && e.response.status === 401) {
            setSessionExpired(true);
            throw new Error("Your session has expired. Please login again with Google Marketplace.");
          }
          
          // For other errors, log as usual
          ApiErrorHandler(
            e.message,
            e.config?.url || '',
            JSON.stringify(e.config?.headers || {}),
            e.config?.method || ''
          );
          throw e; // Re-throw to be caught by the outer try/catch
        });
      
      const data = response;
      
      if (data.token) {
        // Sign in with custom token
        const auth = getAuth();
        await signInWithCustomToken(auth, data.token);
        
        // Get user token and store it
        const userToken = await auth.currentUser?.getIdToken();
        if (userToken) {
          window.localStorage.setItem("token", userToken);
          
          // Decode token to get user info
          const decodedToken = getJWTDecoded(userToken);
          
          // Store user info in localStorage
          window.localStorage.setItem("role", decodedToken.role?.toString() || "1");
          window.localStorage.setItem("uuid", decodedToken.uuid || "");
          window.localStorage.setItem("name", decodedToken.name || "");
          
          window.localStorage.setItem("tenantId", data.tenantId || "");
          
          // Update global context with tenant data if available
          if (data.tenantData) {
            setTenantData(data.tenantData);
          } else if (data.tenantId) {
            // If tenantData is not directly available but we have tenantId,
            // the GlobalContext will fetch it automatically since we stored tenantId in localStorage
          }
          
          // Redirect to dashboard
          navigate("/private/userProfile/");
        }
      } else {
        setError("Authentication failed: No token received");
      }
    } catch (err) {
      // Handle error without console.error to avoid lint issues
      setError(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  
  // Handle Google Marketplace SSO login
  const handleGoogleMarketplaceLogin = () => {
    setError(null);
    setSessionExpired(false);
    
    window.open("https://console.cloud.google.com/marketplace", "_blank");
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-md">
        <div className="flex justify-center">
          <img src={nexumLogo} alt="Nexum Logo" className="h-16" />
        </div>
        
        <h2 className="mt-6 text-3xl font-extrabold text-center text-gray-900">
          Google Cloud Marketplace
        </h2>
        <p className="mt-2 text-sm text-center text-gray-600">
          Sign in with your Google Cloud Marketplace account
        </p>
        
        {error && (
          <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">
            {error}
          </div>
        )}
        
        <div className="mt-8 space-y-6">

          {!sessionExpired && (
            <Button2
              onClick={handleMarketplaceLogin}
              disabled={loading || !tokenId}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title={loading ? "Signing in..." : "Sign in with Google Cloud Marketplace"}
            />
          )}

          
          
          {sessionExpired && (
            <Button2
              onClick={handleGoogleMarketplaceLogin}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              title="Go to Google Marketplace"
            />
          )}
          
          <div className="text-sm text-center">
            <button
              onClick={() => navigate("/signin")}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Back to standard login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceLogin;
